import { useState } from 'react';
import type { DistributionChannelSummary, DistributionOverview } from '../../api';
import {
  checkDistributionChannelHealth,
  regenerateDistributionFeed,
  startDistributionRun,
} from '../../api';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { Checklist, SummaryTile, SummaryTiles, Table } from '../primitives';
import { OperationSummary, SummaryFact, SummaryFacts } from '../operation/OperationSummary';
import { actionLabel } from '../operation/actionLabels';
import {
  channelHealthLabel,
  channelHealthLevel,
  channelLabel,
  driftLabel,
  feedHealthLabel,
  feedHealthLevel,
  feedIngestionHelp,
  feedIngestionLabel,
  feedIngestionLevel,
  formatBytes,
  formatWhen,
  relativeWhen,
  runKindLabel,
  runStateLabel,
} from './labels';

/**
 * The distribution overview: how much of the catalogue is actually out
 * there, one card per channel, and what the last runs did.
 *
 * The counts are stated as words as well as numbers, because "34 warnings"
 * only helps if it is obvious that a warning is live-but-worth-looking-at
 * and a rejection is not live at all.
 */
export function DistributionOverviewPanel({
  overview,
  onOpenChannel,
  onOpenProducts,
  onRefresh,
  projectId,
  canManage,
}: {
  overview: DistributionOverview;
  onOpenChannel: (connectionId: string) => void;
  onOpenProducts: (filter: { state?: string; channel?: string }) => void;
  onRefresh: () => void;
  projectId: string;
  canManage: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summary = overview.summary;
  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  const runningRun = overview.runs.find((entry) => entry.state === 'running');
  // Until the catalogue has been read there is nothing to count: the page
  // leads with the setup and the one action that moves it on (CO-161).
  const catalogueEmpty = summary.products === 0;
  const lastRun = overview.runs[0];
  const lastRunFailed = lastRun?.state === 'failed' || lastRun?.state === 'partial';
  const importButton = (primary: boolean) =>
    canManage ? (
      <button
        type="button"
        className={`button ${primary ? 'button--primary' : 'button--secondary'}`}
        disabled={busy !== null || runningRun !== undefined}
        title={runningRun ? 'A catalogue run is already in progress.' : undefined}
        onClick={() => run('sync', () => startDistributionRun(projectId, 'initial_sync'))}
        data-testid="distribution-sync"
      >
        {busy === 'sync' || runningRun?.kind === 'initial_sync' ? <span className="spinner" aria-hidden="true" /> : null}
        {busy === 'sync' ? 'Starting' : runningRun?.kind === 'initial_sync' ? 'Importing the catalogue' : primary ? 'Import catalogue' : 'Read the catalogue again'}
      </button>
    ) : null;

  return (
    <>
      {error ? (
        <PanelCard tone="critical">
          <p className="panel__note">{error}</p>
        </PanelCard>
      ) : null}

      {catalogueEmpty ? (
        <PanelCard labelledBy="distribution-setup-title" testId="distribution-setup-progress">
          <SectionHeader id="distribution-setup-title" title="Get the products out" actions={importButton(true)} />
          <Checklist
            label="Distribution setup"
            testId="distribution-setup-steps"
            items={[
              {
                key: 'channel',
                label: overview.channels.length > 0 ? `${overview.channels.length === 1 ? 'A channel is' : `${overview.channels.length} channels are`} connected` : 'Connect a channel',
                state: overview.channels.length > 0 ? 'done' : 'todo',
              },
              {
                key: 'catalogue',
                label: runningRun?.kind === 'initial_sync' ? 'Importing the catalogue' : 'Import the catalogue',
                state: 'todo',
              },
              { key: 'publish', label: 'Publish to each channel', state: 'todo' },
            ]}
          />
          {!canManage ? <p className="muted">Only someone who manages distribution can import the catalogue.</p> : null}
        </PanelCard>
      ) : null}

      {catalogueEmpty ? null : (
      <SummaryTiles label="Products in numbers">
        <SummaryTile
          label="Products"
          level="unknown"
          headline={<span className="tabular-nums">{summary.products}</span>}
          detail={summary.withdrawn > 0 ? `${summary.withdrawn} no longer offered by the shop` : undefined}
          testId="distribution-products"
        />
        <SummaryTile
          label="Live"
          level={summary.healthy > 0 ? 'ok' : 'unknown'}
          headline={<span className="tabular-nums">{summary.healthy}</span>}
          testId="distribution-live"
        />
        <SummaryTile
          label="Need attention"
          level={summary.rejected + summary.failed > 0 ? 'critical' : summary.warnings > 0 ? 'warning' : 'ok'}
          headline={<span className="tabular-nums">{summary.warnings + summary.rejected + summary.failed}</span>}
          detail={
            summary.warnings + summary.rejected + summary.failed > 0
              ? `${summary.warnings} warnings · ${summary.rejected} rejected · ${summary.failed} could not be sent`
              : undefined
          }
          testId="distribution-attention"
        />
        <SummaryTile
          label="Not distributed"
          level={summary.not_eligible > 0 ? 'warning' : 'ok'}
          headline={<span className="tabular-nums">{summary.not_eligible}</span>}
          detail={summary.unsupported > 0 ? `${summary.unsupported} of a product type Commerce Ops does not publish` : undefined}
          testId="distribution-not-eligible"
        />
      </SummaryTiles>
      )}

      <PanelCard labelledBy="distribution-channels-title">
        <SectionHeader
          id="distribution-channels-title"
          title="Channels"
          actions={catalogueEmpty ? undefined : importButton(false)}
        />
        {overview.channels.length === 0 ? (
          <QuietEmpty testId="distribution-no-channels">No channel connected</QuietEmpty>
        ) : (
          <div className="distribution-channels">
            {overview.channels.map((channel) => (
              <ChannelCard
                key={channel.connection_id}
                channel={channel}
                busy={busy}
                canManage={canManage}
                onOpen={() => onOpenChannel(channel.connection_id)}
                onOpenProducts={onOpenProducts}
                onCheckHealth={() =>
                  run(`health-${channel.connection_id}`, () => checkDistributionChannelHealth(channel.connection_id))
                }
                onRegenerate={() =>
                  run(`feed-${channel.connection_id}`, () => regenerateDistributionFeed(channel.connection_id))
                }
              />
            ))}
          </div>
        )}
      </PanelCard>

      <PanelCard labelledBy="distribution-runs-title">
        <SectionHeader
          id="distribution-runs-title"
          title="Catalogue and reconciliation runs"
          description={
            // A failed run is told by the block below; the line would say it twice.
            lastRun && !lastRunFailed
              ? `${runKindLabel[lastRun.kind] ?? lastRun.kind} · ${runStateLabel[lastRun.state] ?? lastRun.state} · ${relativeWhen(lastRun.started_at)}${lastRun.state === 'running' ? '' : ` · ${lastRun.stats.repaired} repaired`}`
              : undefined
          }
          help="Reconciliation runs every night. It compares the shop, Commerce Ops and each channel, and repairs what it safely can."
          helpLabel="How reconciliation works"
          actions={
            canManage ? (
              <button
                type="button"
                className="button button--secondary"
                disabled={busy !== null || runningRun !== undefined}
                onClick={() => run('reconcile', () => startDistributionRun(projectId, 'reconciliation'))}
                data-testid="distribution-reconcile"
              >
                {busy === 'reconcile' ? 'Starting' : 'Reconcile now'}
              </button>
            ) : null
          }
        />
        {lastRun && lastRunFailed ? (
          <OperationSummary
            tone={lastRun.state === 'failed' ? 'danger' : 'warning'}
            title={`${runKindLabel[lastRun.kind] ?? lastRun.kind} ${lastRun.state === 'failed' ? 'failed' : 'finished with failures'}`}
            testId="distribution-run-problem"
            actions={
              <>
                {lastRun.stats.failed > 0 || summary.failed > 0 ? (
                  <button type="button" className="button button--secondary button--small" onClick={() => onOpenProducts({ state: 'failed' })} data-testid="distribution-view-failed">
                    View failed products
                  </button>
                ) : null}
                {canManage && (lastRun.kind === 'initial_sync' || lastRun.kind === 'reconciliation') ? (
                  <button
                    type="button"
                    className="button button--secondary button--small"
                    disabled={busy !== null || runningRun !== undefined}
                    onClick={() => run('retry', () => startDistributionRun(projectId, lastRun.kind))}
                    data-testid="distribution-run-retry"
                  >
                    {busy === 'retry' ? 'Starting' : actionLabel.retry}
                  </button>
                ) : null}
              </>
            }
          >
            <SummaryFacts>
              <SummaryFact label="Started">{relativeWhen(lastRun.started_at)}</SummaryFact>
              <SummaryFact label="Products" testId="distribution-run-products">
                {lastRun.stats.failed > 0
                  ? `${lastRun.stats.failed} could not be sent in this run`
                  : 'This run did not record how many could not be sent'}
                {summary.failed > 0 ? ` · ${summary.failed} could not be sent now` : ''}
              </SummaryFact>
              <SummaryFact label="Cause" testId="distribution-run-cause">
                {lastRun.error || 'Not recorded. Each product that could not be sent says why.'}
              </SummaryFact>
            </SummaryFacts>
          </OperationSummary>
        ) : null}
        {overview.runs.length === 0 ? (
          <QuietEmpty testId="distribution-no-runs">No run yet</QuietEmpty>
        ) : (
          // The history is there when it is useful: open when the last run
          // failed, one click away otherwise (CO-161).
          <details className="quiet-disclosure distribution-runs" open={lastRunFailed} data-testid="distribution-runs">
            <summary>Run history ({overview.runs.length})</summary>
          <Table label="Recent distribution runs" compact className="distribution-runs-table">
            <thead>
              <tr>
                <th scope="col">Run</th>
                <th scope="col">State</th>
                <th scope="col">Inspected</th>
                <th scope="col">Queued</th>
                <th scope="col">Repaired</th>
                <th scope="col">Started</th>
              </tr>
            </thead>
            <tbody>
              {overview.runs.map((entry) => (
                <tr key={entry.id}>
                  <th scope="row">{runKindLabel[entry.kind] ?? entry.kind}</th>
                  <td>
                    <LevelBadge
                      level={
                        entry.state === 'failed'
                          ? 'critical'
                          : entry.state === 'partial'
                            ? 'warning'
                            : entry.state === 'running'
                              ? 'unknown'
                              : 'ok'
                      }
                    >
                      {runStateLabel[entry.state] ?? entry.state}
                    </LevelBadge>
                    {entry.error && entry.id !== lastRun?.id ? <p className="distribution-run-error">{entry.error}</p> : null}
                  </td>
                  <td className="tabular-nums">{entry.stats.inspected}</td>
                  <td className="tabular-nums">{entry.stats.queued}</td>
                  <td className="tabular-nums">{entry.stats.repaired}</td>
                  <td>{formatWhen(entry.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          </details>
        )}
        {overview.runs[0]?.findings?.length ? (
          <details className="distribution-findings">
            <summary>What the last run found ({overview.runs[0].findings.length})</summary>
            <ul>
              {overview.runs[0].findings.slice(0, 20).map((finding, index) => (
                <li key={`${finding.kind}-${finding.subject}-${index}`}>
                  <strong>{finding.subject}</strong> — {driftLabel[finding.kind] ?? finding.kind}
                  {finding.action ? `. ${finding.action}.` : '.'}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </PanelCard>
    </>
  );
}

/**
 * One channel's card: how much of the catalogue it carries, its own health,
 * and — for a feed channel — the state of the feed Commerce Ops serves.
 */
function ChannelCard({
  channel,
  busy,
  canManage,
  onOpen,
  onOpenProducts,
  onCheckHealth,
  onRegenerate,
}: {
  channel: DistributionChannelSummary;
  busy: string | null;
  canManage: boolean;
  onOpen: () => void;
  onOpenProducts: (filter: { state?: string; channel?: string }) => void;
  onCheckHealth: () => void;
  onRegenerate: () => void;
}) {
  const health = channel.enabled ? channel.health : 'disabled';
  return (
    <section className="distribution-channel" aria-label={`${channelLabel[channel.channel_type]}, ${channel.scope}`}>
      <header className="distribution-channel__head">
        <div>
          <h3 className="distribution-channel__title">{channelLabel[channel.channel_type]}</h3>
          <p className="distribution-channel__scope">{channel.scope}</p>
        </div>
        <LevelBadge level={channelHealthLevel[health]}>{channelHealthLabel[health]}</LevelBadge>
      </header>

      <p className="distribution-channel__count">
        <span className="tabular-nums">{channel.published}</span>
        <span className="distribution-channel__of"> of {channel.in_scope} published</span>
      </p>

      <dl className="distribution-channel__facts">
        {channel.warnings > 0 ? (
          <div>
            <dt>Warnings</dt>
            <dd>
              <button type="button" className="link-button" onClick={() => onOpenProducts({ state: 'warning', channel: channel.channel_type })}>
                {channel.warnings}
              </button>
            </dd>
          </div>
        ) : null}
        {channel.rejected > 0 ? (
          <div>
            <dt>Rejected</dt>
            <dd>
              <button type="button" className="link-button" onClick={() => onOpenProducts({ state: 'rejected', channel: channel.channel_type })}>
                {channel.rejected}
              </button>
            </dd>
          </div>
        ) : null}
        {channel.pending > 0 ? (
          <div>
            <dt>Pending</dt>
            <dd className="tabular-nums">{channel.pending}</dd>
          </div>
        ) : null}
        {channel.failed > 0 ? (
          <div>
            <dt>Could not be sent</dt>
            <dd>
              <button type="button" className="link-button" onClick={() => onOpenProducts({ state: 'failed', channel: channel.channel_type })}>
                {channel.failed}
              </button>
            </dd>
          </div>
        ) : null}
        {channel.ineligible > 0 ? (
          <div>
            <dt>Not distributed</dt>
            <dd>
              <button type="button" className="link-button" onClick={() => onOpenProducts({ state: 'ineligible', channel: channel.channel_type })}>
                {channel.ineligible}
              </button>
            </dd>
          </div>
        ) : null}
      </dl>

      {channel.feed ? (
        <div className="distribution-channel__feed">
          <LevelBadge level={feedHealthLevel[channel.feed.health]}>{feedHealthLabel[channel.feed.health]}</LevelBadge>
          <p className="distribution-channel__feed-detail">
            {channel.feed.eligible_count} products · {formatBytes(channel.feed.byte_size)} · generated{' '}
            {relativeWhen(channel.feed.generated_at)}
            {channel.feed.excluded_count > 0 ? ` · ${channel.feed.excluded_count} excluded` : ''}
          </p>
          {channel.feed.detail ? <p className="distribution-channel__feed-detail">{channel.feed.detail}</p> : null}

          {/*
            Generating the feed and having it accepted are two different
            facts, so they get two badges. A healthy feed whose products are
            being refused is exactly the state one combined indicator would
            hide.
          */}
          {channel.feed.ingestion !== 'not_reported' ? (
            <>
              <LevelBadge level={feedIngestionLevel[channel.feed.ingestion]}>
                {feedIngestionLabel[channel.feed.ingestion]}
              </LevelBadge>
              {channel.feed.ingestion_detail || feedIngestionHelp[channel.feed.ingestion] || channel.feed.ingested_at ? (
                <p className="distribution-channel__feed-detail">
                  {channel.feed.ingestion_detail || feedIngestionHelp[channel.feed.ingestion]}
                  {channel.feed.ingested_at
                    ? `${channel.feed.ingestion_detail || feedIngestionHelp[channel.feed.ingestion] ? ' · ' : ''}Collected ${relativeWhen(channel.feed.ingested_at)}`
                    : ''}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : (
        <p className="distribution-channel__feed-detail">
          {channel.last_success_at ? `Last send · ${relativeWhen(channel.last_success_at)}` : 'Nothing sent yet'}
        </p>
      )}

      {channel.health_detail ? <p className="panel__note">{channel.health_detail}</p> : null}

      <div className="distribution-channel__actions">
        <button type="button" className="button button--secondary" onClick={onOpen} data-testid={`channel-settings-${channel.channel_type}`}>
          Settings
        </button>
        {canManage ? (
          <button
            type="button"
            className="link-button"
            disabled={busy === `health-${channel.connection_id}`}
            onClick={onCheckHealth}
          >
            {busy === `health-${channel.connection_id}` ? 'Checking' : 'Check connection'}
          </button>
        ) : null}
        {canManage && channel.feed ? (
          <button
            type="button"
            className="link-button"
            disabled={busy === `feed-${channel.connection_id}`}
            onClick={onRegenerate}
          >
            {busy === `feed-${channel.connection_id}` ? 'Queued' : 'Regenerate feed'}
          </button>
        ) : null}
      </div>
    </section>
  );
}
